// The live WebSocket contract (Sprint 9, docs/10 §Live Eyes). The socket is a
// read-only presence enhancement over the REST fleet: the API pushes which
// connected players are online and what they're showing right now, plus a
// "snapshot ready" nudge. Framing is architect-fixed and shared with the API
// worker (B1) — keep these shapes in lockstep with the server.
//
// This module is framework-free (no React) so the parser + socket are unit
// testable with a fake WebSocket.

/** What a screen is showing right now, as reported over the socket. `name` is a
 *  best-effort human label (playlist / media); absent when the server doesn't
 *  resolve one — the UI then falls back to the REST last_state. */
export interface NowPlaying {
  media_id: string;
  name?: string;
}

/** One screen's presence in a full snapshot (only connected players appear). */
export interface PresenceScreen {
  screen_id: string;
  online: boolean;
  now_playing: NowPlaying | null;
}

/** Server → client frames. The socket is otherwise read-only. */
export type InboundMessage =
  | { type: 'presence'; screens: PresenceScreen[] }
  | { type: 'presence_delta'; screen_id: string; online: boolean; now_playing: NowPlaying | null }
  | { type: 'snapshot_ready'; screen_id: string; snapshot_id: string }
  | { type: 'ping' };

/** Client → server frames. Only the keep-alive reply — never a mutation. */
export type OutboundMessage = { type: 'pong' };

const INBOUND_TYPES = new Set(['presence', 'presence_delta', 'snapshot_ready', 'ping']);

/**
 * Parse a raw socket payload into a typed inbound message, or null when it is
 * malformed / an unknown type (the caller ignores unknowns rather than crashing —
 * a forward-compatible socket may add frames the current client doesn't handle).
 */
export function parseInbound(data: unknown): InboundMessage | null {
  if (typeof data !== 'string') return null;
  let obj: unknown;
  try {
    obj = JSON.parse(data);
  } catch {
    return null;
  }
  if (!obj || typeof obj !== 'object') return null;
  const type = (obj as { type?: unknown }).type;
  if (typeof type !== 'string' || !INBOUND_TYPES.has(type)) return null;
  return obj as InboundMessage;
}
